import { forwardRef, type ReactNode } from 'react';
import { ArrowLeft, CircleHelp, X } from 'lucide-react';

import { Button, type ButtonProps } from '@/components/ui/Button';
import {
  DialogClose,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@shared';

interface ResponsiveDialogHeaderProps {
  title: ReactNode;
  titleAccessibleName?: string;
  description?: ReactNode;
  onBack?: () => void;
  backLabel?: string;
  right?: ReactNode;
  showRightOnDesktop?: boolean;
  className?: string;
  testId?: string;
}

/**
 * Header used by workflows that become full-screen on mobile. It keeps the
 * desktop dialog title/description while presenting a mobile app-bar with a
 * back action, centered title and optional right-side action.
 */
export function ResponsiveDialogHeader({
  title,
  titleAccessibleName,
  description,
  onBack,
  backLabel = 'Back',
  right,
  showRightOnDesktop = false,
  className,
  testId,
}: ResponsiveDialogHeaderProps) {
  const backButton = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-9 w-9 shrink-0"
      aria-label={backLabel}
      onClick={onBack}
    >
      <ArrowLeft className="h-5 w-5" aria-hidden="true" />
    </Button>
  );

  return (
    <DialogHeader
      data-testid={testId}
      className={cn(
        'relative shrink-0 border-b px-4 py-3 text-left sm:border-0 sm:p-0',
        className,
      )}
    >
      <div className="relative flex min-h-10 items-center justify-between gap-3">
        <div className="relative z-10 flex min-w-9 justify-start sm:hidden">
          {onBack ? (
            backButton
          ) : (
            <DialogClose asChild>{backButton}</DialogClose>
          )}
        </div>
        <DialogTitle
          aria-label={titleAccessibleName}
          className="pointer-events-none absolute inset-x-0 top-1/2 z-0 flex -translate-y-1/2 justify-center truncate px-1 py-2 text-center text-base font-semibold sm:pointer-events-auto sm:static sm:z-auto sm:min-w-0 sm:flex-1 sm:translate-y-0 sm:text-left sm:text-lg"
        >
          {title}
        </DialogTitle>
        <div
          className={cn(
            'relative z-10 flex min-w-9 shrink-0 items-center justify-end',
            showRightOnDesktop ? 'sm:flex' : 'sm:hidden',
          )}
        >
          {right ?? <span className="h-9 w-9" aria-hidden="true" />}
        </div>
      </div>
      {description && (
        <DialogDescription className="mt-1 hidden sm:block">
          {description}
        </DialogDescription>
      )}
    </DialogHeader>
  );
}

export function ResponsiveDialogHelpButton({
  label = 'Help',
}: {
  label?: string;
}) {
  return (
    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={label}>
      <CircleHelp className="h-5 w-5" aria-hidden="true" />
    </Button>
  );
}

/**
 * Shared square treatment for compact dialog-header actions. Keeping this
 * style in one component prevents menu and close controls from drifting apart
 * when a dialog header is rendered at different breakpoints.
 */
export const ResponsiveDialogHeaderActionButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'size' | 'variant'>
>(function ResponsiveDialogHeaderActionButton({ className, type = 'button', ...props }, ref) {
  return (
    <Button
      ref={ref}
      type={type}
      variant="outline"
      size="icon"
      className={cn(
        'h-9 w-9 shrink-0 rounded-md border-input bg-background shadow-sm',
        className,
      )}
      {...props}
    />
  );
});

ResponsiveDialogHeaderActionButton.displayName = 'ResponsiveDialogHeaderActionButton';

export function ResponsiveDialogHeaderActions({
  children,
  className,
  testId,
}: {
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <div data-testid={testId} className={cn('flex items-center gap-2', className)}>
      {children}
    </div>
  );
}

/** A matching close action for headers that opt out of DialogContent's default X. */
export function ResponsiveDialogHeaderCloseButton({
  label = 'Close',
  className,
  testId,
}: {
  label?: string;
  className?: string;
  testId?: string;
}) {
  return (
    <DialogClose asChild>
      <ResponsiveDialogHeaderActionButton
        aria-label={label}
        className={className}
        data-testid={testId}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </ResponsiveDialogHeaderActionButton>
    </DialogClose>
  );
}
